// The ONE block-label resolver for swimlane rows. The live SwimlaneView feeds
// it to `swimlaneGeometry` (sizing) and paints its output verbatim, and the
// geometry-parity test holds the headless export's `blockLabel`
// (viewer-core/process-swimlane-export.ts) equal to it row-for-row — so label
// text, wrap, and box heights cannot drift between the live view and SVG/PNG
// exports.

import type { Row, SwimlaneLayout, WalkResult } from "@archlang/engine";
import { displayInterfaceNameString } from "../displayNames";

export interface BlockLabel {
  /** Main text, rendered at the standard font size. */
  readonly primary: string;
  /** Optional secondary line, rendered smaller + muted below primary.
   *  Used by Flow to surface the caller as "from <Module>" so it
   *  doesn't read like the interface belongs to the caller. */
  readonly subtitle?: string;
}

export function labelForStep(
  row: Row,
  walk: WalkResult | null,
  variant: SwimlaneLayout["variant"],
  laneLabel: (id: string) => string,
): BlockLabel {
  if (row.stepIndex === null) {
    return { primary: row.terminator ? row.terminator.reason ?? "" : "" };
  }
  if (!walk) return { primary: `#${row.stepIndex + 1}` };
  const step = walk.byIndex.get(row.stepIndex);
  if (!step) return { primary: `#${row.stepIndex + 1}` };
  // Re-entry BEFORE noteText: the walker stamps `go <target>` as noteText on
  // the same step, but both BlockCell and the headless export paint (and the
  // geometry must therefore size) the `↩ <target>` form.
  if (step.reentryTarget) return { primary: `↩ ${step.reentryTarget}` };
  if (step.noteText) return { primary: step.noteText };
  const ifaceName = displayInterfaceNameString(
    step.to[step.to.length - 1] ?? step.to.join("."),
  );
  if (variant === "sequence") {
    // Omit empty argument parens — `iface` reads better than `iface()`
    // when there are no arguments to surface.
    const args = step.args && step.args.length > 0 ? `(${step.args.join(", ")})` : "";
    const bind = step.bind ? `${step.bind} = ` : "";
    return { primary: `${bind}${ifaceName}${args}` };
  }
  // Flow: interface name on top, caller as muted subtitle so the
  // ownership reads correctly (iface belongs to the callee lane).
  const callerName = laneLabel(row.fromLaneId);
  if (!callerName || row.fromLaneId === row.toLaneId) return { primary: ifaceName };
  return { primary: ifaceName, subtitle: `from ${callerName}` };
}
